import { Chart } from 'components/Chart/Chart'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { Cards } from 'modules/Statistics/Pulse/components/Cards'
import { COLORS } from 'modules/Statistics/Pulse/config/colors'
import styles from 'modules/Statistics/Pulse/Pulse.module.scss'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Statistics/Pulse/config/filters'
import { useTranslation } from 'react-i18next'
import { Button, Grid } from 'gu-ui-kit'
import Switch from 'components/Switch'
import clsx from 'clsx'
import PageHeader from 'components/PageHeader'

import {
  PULSE_FETCH_DATA,
  PULSE_SET_CHART_TYPE,
  PULSE_SET_FILTER,
  PULSE_SET_NEW_METRIC,
  PULSE_SETUP_INITIAL_METRICS,
  PULSE_TOGGLE_METRIC_HIDDEN,
} from 'modules/Statistics/types'

const initialMetrics = [
  { id: 'cost', isActive: true },
  { id: 'impressions', isActive: true },
  { id: 'clicks', isActive: true },
  { id: 'avg_click_price', isActive: true },
  { id: 'google_analytics_metrics.transactions', isActive: false },
  { id: 'google_analytics_metrics.transaction_revenue', isActive: false },
  { id: 'google_analytics_metrics.roi', isActive: false },
]

const Pulse = () => {
  const dispatch = useDispatch()
  const [metricInited, setMetricInited] = useState(false)
  const { t } = useTranslation()
  const [isDemoMode, setIsDemoMode] = useLocalStorage('isDemoMode', true)
  const [metricsConfig, setMetricsConfig] = useLocalStorage('pulseMetricsConfig', initialMetrics)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['statistics/pulse'].filters),
    config: config(),
    additionalFilterState: useSelector((state) => state['statistics/filters'].filters),
    setAction: PULSE_SET_FILTER,
    nonClearableFilters: ['date_group_period', 'include_vat'],
    localStorageKey: 'pulse',
  })

  const {
    summary = {},
    metrics,
    items = [],
    loading,
    chartType,
    measure,
  } = useSelector(
    (state) => ({
      summary: state['statistics/pulse'].analytics.data.summary,
      metrics: state['statistics/pulse'].metrics,
      items: state['statistics/pulse'].analytics.data.items,
      loading: state['statistics/pulse'].analytics.loading,
      chartType: state['statistics/pulse'].chartType,
      measure: state['statistics/pulse'].measure,
    }),
    shallowEqual,
  )

  useEffect(
    () =>
      dispatch({
        type: PULSE_SETUP_INITIAL_METRICS,
        payload: { metrics: metricsConfig },
      }),
    [],
  )

  useEffect(() => setMetricsConfig(metrics), [metrics])

  const [filtersState, setFiltersState] = useState(omit(filterManager.filters, ['include_vat']))

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setFiltersState(omittedFilters)
  }, [filterManager.filters])

  useEffect(
    () => filterManager.filters && dispatch({ type: PULSE_FETCH_DATA, isDemoMode }),
    [filterManager.filters, isDemoMode],
  )

  useEffect(() => {
    const availableMetricsKeys = Object.keys(summary)

    if (!metricInited && availableMetricsKeys.length > 0 && !metrics?.length) {
      setMetricInited(true)
      dispatch({
        type: PULSE_SET_NEW_METRIC,
        payload: { metric: availableMetricsKeys[0], isActive: true },
      })
    }
  }, [summary, metrics])

  return (
    <>
      <Grid className={clsx(styles['header-container'], 'margin-bottom-32', 'layout-tile')}>
        <PageHeader>{t('common::nav_menu.pulse')}</PageHeader>

        <Switch
          label={t('statistics::demo_mode_switcher')}
          checked={isDemoMode}
          onChange={() => {
            setIsDemoMode(!isDemoMode)
          }}
          className={styles['demo-switcher']}
        />
      </Grid>

      <div className={'margin-bottom-32 layout-tile'}>
        {filterManager.filters && (
          <FilterPanel
            filterManager={filterManager}
            className={styles['pulse__filterPanel']}
          />
        )}

        <Chart
          emptyDataLabel={{
            text: t('statistics::chart.empty_data.text', {
              context: isDemoMode ? 'demo' : '',
            }),
            actionButton: !isDemoMode ? (
              <Button
                variant={'primary'}
                size={'m'}
                onClick={() => setIsDemoMode(true)}
              >
                {t('statistics::demo_mode_switcher')}
              </Button>
            ) : null,
          }}
          colors={COLORS}
          className={'margin-bottom-32'}
          chartType={chartType}
          items={items}
          metrics={metrics}
          loading={loading}
          date_group_period={{
            value: filterManager.filters?.date_group_period ?? 'day',
            options: filterManager.configuration.date_group_period.options,
          }}
          include_vat={filterManager.filters?.include_vat}
          summary={summary}
          measure={measure}
          changeChartPeriod={(value) =>
            filterManager.updateFilter({
              fieldName: 'date_group_period',
              value: value,
            })
          }
          toggleMetric={(id) => dispatch({ type: PULSE_TOGGLE_METRIC_HIDDEN, payload: { id } })}
          changeChartType={() =>
            dispatch({
              type: PULSE_SET_CHART_TYPE,
              payload: { type: chartType === 'Line' ? 'Bar' : 'Line' },
            })
          }
        />
      </div>
      <div className={'layout-tile'}>
        <Cards className={clsx(styles['pulse__cards'], 'layout-tile')} />
      </div>
    </>
  )
}

Pulse.propTypes = {
  filterPortalRef: PropTypes.object,
}

export default Pulse
